import React, { useState } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  ScrollView,
  Image,
  Platform,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../ThemedText";
import { ThemedChip } from "../ThemedChip";
import { ThemedPlatformSymbol } from "../ThemedPlatformSymbol";
import { ThemedCompactCard } from "./ThemedCompactCard";
import { ThemedKebabMenu, type IKebabActionItem } from "../ThemedKebabMenu";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useComponentTheme } from "@/hooks/useComponentTheme";
import { CardEntityType, formatCardTimestamp } from "common247";
import { getEntityIcon, navigateToCardEntity } from "./card-helpers";

/**
 * Props for the ThemedMediumCard component.
 */
export interface IThemedMediumCardProps {
  /** Entity domain type */
  entityType: CardEntityType;
  /** Primary identifier */
  id?: string;
  /** Primary title or headline */
  title: string;
  /** Chronological date string */
  date?: string;
  /** Formatted date display string */
  formattedDate?: string;
  /** Creation timestamp */
  createdAt?: number;
  /** Cover / Avatar image thumbnail URL */
  thumbnailUrl?: string;
  /** Short summary or description narrative */
  description?: string;
  /** Author / Owner display name */
  ownerDisplayName?: string;
  /** Whether the user is the owner */
  isOwner?: boolean;
  /** Whether the item is the logged-on AppUser / self-contact */
  isSelf?: boolean;
  /** Whether the item has a 5-star rating */
  isFiveStar?: boolean;
  /** Whether the item is shared with the current user */
  isShared?: boolean;
  /** Actions for trailing kebab menu */
  kebabActions?: IKebabActionItem[];
  /** Whether selection mode is active */
  selectionMode?: boolean;
  /** Whether the card is selected */
  isSelected?: boolean;
  /** Selection toggle callback */
  onSelect?: () => void;
  /** Long press callback */
  onLongPress?: () => void;
  /** Context menu callback (web) */
  onContextMenu?: (e?: any) => void;
  /** Optional custom child components */
  children?: React.ReactNode;
  /** Tag type for tag entities */
  tagType?: string;
  /** Associated media items */
  mediaList?: any[];
  /** Associated linked contacts */
  contactsList?: any[];
  /** Associated linked groups */
  groupsList?: any[];
  /** Associated linked memories */
  memoriesList?: any[];
  /** Associated linked places */
  placesList?: any[];
  /** Associated linked anchors */
  anchorsList?: any[];
  /** Associated tags */
  tagsList?: any[];
  /** Associated shares */
  sharesList?: any[];
  /** Press handler */
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/**
 * ThemedMediumCard: Tier 3 Item Card component.
 *
 * Balanced catalog card displaying:
 * - Header row with thumbnail / avatar / icon, Title, Date, and Platform Symbols
 * - Short narrative preview
 * - Media Highlights tray (Compact cards)
 * - **Links (Contacts, Groups, Memories, Places, Anchors) as a unified single section** (Chip cards)
 * - Tags tray (Tangerine Chip cards)
 * - Shares tray (Heliotrope Chip cards)
 */
export function ThemedMediumCard({
  entityType,
  id,
  title,
  date,
  formattedDate,
  createdAt,
  thumbnailUrl,
  description,
  ownerDisplayName,
  isOwner = false,
  isSelf = false,
  isFiveStar = false,
  isShared = false,
  kebabActions,
  selectionMode = false,
  isSelected = false,
  onSelect,
  onLongPress,
  onContextMenu,
  children,
  tagType,
  mediaList = [],
  contactsList = [],
  groupsList = [],
  memoriesList = [],
  placesList = [],
  anchorsList = [],
  tagsList = [],
  sharesList = [],
  onPress,
  style,
  testID,
}: IThemedMediumCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const { card: cardTheme } = useComponentTheme();
  const cardBg = cardTheme.chrome.backgroundColor;
  const borderColor = cardTheme.chrome.restingBorderColor;
  const hoverBorderColor = cardTheme.chrome.hoverBorderColor;
  const tintColor = cardTheme.chrome.selectedBorderColor;
  const textColor = cardTheme.chrome.textColor;
  const textMuted = cardTheme.chrome.textMutedColor;
  const badgeBg = cardTheme.chrome.badgeBgColor;

  const displayDate = formattedDate || date;
  const normalizedContacts = Array.isArray(contactsList) ? contactsList : Object.values(contactsList || {});
  const normalizedGroups = Array.isArray(groupsList) ? groupsList : Object.values(groupsList || {});
  const normalizedMemories = Array.isArray(memoriesList) ? memoriesList : Object.values(memoriesList || {});
  const normalizedPlaces = Array.isArray(placesList) ? placesList : Object.values(placesList || {});
  const normalizedAnchors = Array.isArray(anchorsList) ? anchorsList : Object.values(anchorsList || {});
  const normalizedMedia = Array.isArray(mediaList) ? mediaList : Object.values(mediaList || {});
  const normalizedTags = Array.isArray(tagsList) ? tagsList : Object.values(tagsList || {});
  const normalizedShares = Array.isArray(sharesList) ? sharesList : Object.values(sharesList || {});
  const totalLinks = normalizedContacts.length + normalizedGroups.length + normalizedMemories.length + normalizedPlaces.length + normalizedAnchors.length;

  /**
   * Handles navigation when the card is pressed.
   */
  const handleCardPress = () => {
    if (selectionMode && onSelect) {
      onSelect();
      return;
    }
    if (onPress) {
      onPress();
      return;
    }
    navigateToCardEntity(entityType, id, "ThemedMediumCard");
  };

  /**
   * Renders the entity-specific leading avatar or icon.
   */
  const renderLeadingIcon = () => {
    if (thumbnailUrl) {
      return (
        <Image
          source={{ uri: thumbnailUrl }}
          style={styles.avatarThumbnail}
          resizeMode="cover"
        />
      );
    }

    const iconName = getEntityIcon(entityType, tagType);

    return (
      <View style={[styles.avatarPlaceholder, { backgroundColor: badgeBg }]}>
        <Ionicons name={iconName} size={18} color={tintColor} />
      </View>
    );
  };

  return (
    <TouchableOpacity
      testID={testID}
      activeOpacity={0.85}
      onPress={handleCardPress}
      onLongPress={onLongPress}
      delayLongPress={350}
      {...(Platform.OS === "web"
        ? ({
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
            ...(onContextMenu
              ? {
                  onContextMenu: (e: any) => {
                    e.preventDefault();
                    onContextMenu(e);
                  },
                }
              : {}),
          } as any)
        : {})}
      style={[
        styles.cardContainer,
        {
          backgroundColor: cardBg,
          borderColor: isSelected ? tintColor : isHovered ? hoverBorderColor : borderColor,
        },
        style,
      ]}
      accessibilityRole={Platform.select({ web: "article" as any, default: undefined })}
      accessibilityLabel={`Medium card: ${title}`}
    >
      {/* 1. Header Row */}
      <View style={styles.headerRow}>
        {selectionMode && (
          <TouchableOpacity
            style={[
              styles.checkbox,
              isSelected && { backgroundColor: tintColor, borderColor: tintColor },
            ]}
            onPress={(e) => {
              e.stopPropagation?.();
              onSelect?.();
            }}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: isSelected }}
          >
            {isSelected && <Ionicons name="checkmark" size={12} color="#FFFFFF" />}
          </TouchableOpacity>
        )}
        {renderLeadingIcon()}
        <View style={styles.headerInfo}>
          <ThemedText
            type="defaultSemiBold"
            numberOfLines={1}
            style={[styles.titleText, { color: textColor }]}
          >
            {title}
          </ThemedText>
          <View style={styles.metaRow}>
            {displayDate && (!createdAt || (displayDate !== formatCardTimestamp(createdAt) && displayDate !== `Created ${formatCardTimestamp(createdAt)}` && displayDate !== `Created: ${formatCardTimestamp(createdAt)}`)) ? (
              <ThemedText style={[styles.metaText, { color: textMuted }]}>
                📅 {displayDate}
              </ThemedText>
            ) : null}
            {createdAt ? (
              <ThemedText style={[styles.metaText, { color: textMuted }]}>
                🕒 Created: {formatCardTimestamp(createdAt)}
              </ThemedText>
            ) : null}
            {ownerDisplayName ? (
              <ThemedText style={[styles.metaText, { color: textMuted }]}>
                • by {ownerDisplayName}
              </ThemedText>
            ) : null}
          </View>
        </View>

        {/* Platform Symbols & Kebab Menu */}
        <View style={styles.symbolsGroup}>
          {isSelf && (
            <ThemedPlatformSymbol type="you" size="small" />
          )}
          {isOwner && (
            <ThemedPlatformSymbol type="owner" size="small" />
          )}
          {isFiveStar && (
            <ThemedPlatformSymbol type="fivestar" size="small" />
          )}
          {isShared && !isOwner && (
            <ThemedPlatformSymbol type="shared" size="small" />
          )}
          {kebabActions && kebabActions.length > 0 && (
            <ThemedKebabMenu
              actions={kebabActions}
              compact={true}
              accessibilityLabel={`Options for ${title}`}
            />
          )}
        </View>
      </View>

      {/* 2. Short Narrative Description */}
      {description ? (
        <ThemedText
          numberOfLines={2}
          style={[styles.descriptionText, { color: textColor }]}
        >
          {description}
        </ThemedText>
      ) : null}

      {children}

      {/* 3. Links Section (Unified Single Section) */}
      {totalLinks > 0 && (
        <View style={[styles.traySection, { borderTopColor: borderColor }]}>
          <View style={styles.trayHeaderRow}>
            <Ionicons name="link-outline" size={11} color={tintColor} />
            <ThemedText type="stamp" style={[styles.trayTitle, { color: textMuted }]}>
              LINKS ({totalLinks})
            </ThemedText>
          </View>
          <View style={styles.chipsWrap}>
            {normalizedContacts.map((c: any, idx: number) => {
              const item = typeof c === "string" ? { id: c, displayName: c } : c;
              const contactId = item.id || item.contactId;
              return (
                <ThemedChip
                  key={`medium-contact-${contactId || idx}`}
                  entityType="contact"
                  id={contactId}
                  label={item.displayName || item.name || "Contact"}
                  thumbnailUrl={item.photoIconUrl || item.photoUrl}
                  isOwner={item.isOwner}
                />
              );
            })}
            {normalizedGroups.map((g: any, idx: number) => {
              const item = typeof g === "string" ? { id: g, name: g } : g;
              const groupId = item.id || item.groupId;
              return (
                <ThemedChip
                  key={`medium-group-${groupId || idx}`}
                  entityType="group"
                  id={groupId}
                  label={item.name || item.displayName || item.title || "Group"}
                />
              );
            })}
            {normalizedMemories.map((m: any, idx: number) => {
              const item = typeof m === "string" ? { id: m, title: "Memory" } : m;
              const memoryId = item.id || item.memoryId;
              return (
                <ThemedChip
                  key={`medium-memory-${memoryId || idx}`}
                  entityType="memory"
                  id={memoryId}
                  label={item.label || item.title || item.name || (item as any)?.memoryTitle || "Memory"}
                  isFiveStar={item.favouriteRating === 5}
                />
              );
            })}
            {normalizedPlaces.map((p: any, idx: number) => {
              const item = typeof p === "string" ? { id: p, name: "Place" } : p;
              const placeId = item.id || item.placeId;
              return (
                <ThemedChip
                  key={`medium-place-${placeId || idx}`}
                  entityType="place"
                  id={placeId}
                  label={item.name || item.displayName || "Place"}
                />
              );
            })}
            {normalizedAnchors.map((a: any, idx: number) => {
              const item = typeof a === "string" ? { id: a, name: "Anchor" } : a;
              const anchorId = item.id || item.anchorId;
              return (
                <ThemedChip
                  key={`medium-anchor-${anchorId || idx}`}
                  entityType="anchor"
                  id={anchorId}
                  label={item.name || item.displayName || "Anchor"}
                />
              );
            })}
          </View>
        </View>
      )}

      {/* 4. Media Highlights (Compact Cards - Suppressed on media cards as the media asset is the primary content) */}
      {entityType !== "media" && mediaList.length > 0 && (
        <View style={[styles.traySection, { borderTopColor: borderColor }]}>
          <ThemedText type="stamp" style={[styles.trayTitle, { color: textMuted }]}>
            MEDIA HIGHLIGHTS ({mediaList.length})
          </ThemedText>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.mediaRow}>
            {mediaList.map((item: any, idx) => (
              <ThemedCompactCard
                key={`medium-media-${item.id || item.mediaId || idx}`}
                entityType="media"
                id={item.id || item.mediaId}
                title={item.title || item.name}
                thumbnailUrl={item.downloadUrl || item.thumbnailUrl || item.url}
                isOwner={item.isOwner}
                isFiveStar={item.isFiveStar || item.favouriteRating === 5}
              />
            ))}
          </ScrollView>
        </View>
      )}

      {/* 5. Tags Section */}
      {normalizedTags.length > 0 && (
        <View style={[styles.traySection, { borderTopColor: borderColor }]}>
          <ThemedText type="stamp" style={[styles.trayTitle, { color: textMuted }]}>
            TAGS ({normalizedTags.length})
          </ThemedText>
          <View style={styles.chipsWrap}>
            {normalizedTags.map((t: any, idx: number) => (
              <ThemedChip
                key={`medium-tag-${t.id || t.tagId || idx}`}
                entityType="tag"
                tagType={t.tagType}
                id={t.id || t.tagId}
                label={t.value || t.name || t.label || "Tag"}
              />
            ))}
          </View>
        </View>
      )}

      {/* 6. Shares Section */}
      {normalizedShares.length > 0 && (
        <View style={[styles.traySection, { borderTopColor: borderColor }]}>
          <ThemedText type="stamp" style={[styles.trayTitle, { color: textMuted }]}>
            SHARED WITH ({normalizedShares.length})
          </ThemedText>
          <View style={styles.chipsWrap}>
            {normalizedShares.map((s: any, idx: number) => (
              <ThemedChip
                key={`medium-share-${s.id || idx}`}
                entityType="share"
                id={s.id}
                label={s.targetTitle || title}
                secondaryLabel={s.recipientName || s.recipientDisplayName}
              />
            ))}
          </View>
        </View>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  cardContainer: {
    borderWidth: 1,
    borderRadius: 4,
    padding: 8,
    gap: 5,
    overflow: "visible",
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 3,
    borderWidth: 1.5,
    borderColor: "#94A3B8",
    alignItems: "center",
    justifyContent: "center",
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    overflow: "visible",
  },
  avatarThumbnail: {
    width: 38,
    height: 38,
    borderRadius: 4,
  },
  avatarPlaceholder: {
    width: 38,
    height: 38,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  headerInfo: {
    flex: 1,
    gap: 2,
  },
  titleText: {
    fontSize: 14,
    lineHeight: 20,
  },
  metaRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
  },
  metaText: {
    fontSize: 11,
  },
  symbolsGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    zIndex: 10,
    overflow: "visible",
  },
  descriptionText: {
    fontSize: 12,
    lineHeight: 17,
  },
  traySection: {
    borderTopWidth: 1,
    paddingTop: 3,
    gap: 2,
  },
  trayHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  trayTitle: {
    fontSize: 9,
    letterSpacing: 0.8,
  },
  mediaRow: {
    flexDirection: "row",
    gap: 4,
    paddingVertical: 0,
  },
  chipsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
});

export default ThemedMediumCard;
